import { initializeDB } from '../config/db';
import { SAFETY_SCHEDULE_STORE, SAFETY_VISIT_OUTBOX_STORE } from '../config/constants';
import axiosInstance from '../axiosInstance';
import { generateUUID } from '../utils/uuid';
import jobSiteSafetyInspectionService from './jobSiteSafetyInspectionService';
import { todayKey } from '../components/safety/utils/scheduleDates';

/**
 * Расписание безопасности на планшете.
 *
 * Читаем только из IndexedDB — сеть на площадках рвётся, и экран должен
 * открываться в любом случае. Записи уходят через очередь: отметку ставят
 * там, где связи нет, а доехать она должна всё равно.
 */

// ─── Чтение ──────────────────────────────────────────────────────────────────

async function getAllAssignments() {
    const db = await initializeDB();
    const tx = db.transaction(SAFETY_SCHEDULE_STORE, 'readonly');
    const all = await tx.objectStore(SAFETY_SCHEDULE_STORE).getAll();
    await tx.done;
    return all || [];
}

/**
 * Назначения, сгруппированные по дням, от понедельника прошлой недели и вперёд.
 * Дни без назначений не выбрасываем: пустой день — это тоже ответ.
 */
export async function getScheduleByDay() {
    const assignments = await getAllAssignments();
    const byDate = new Map();

    for (const item of assignments) {
        // Заметка дня, недели Group View и сведения о человеке лежат в том же
        // сторе, но назначениями не являются.
        if (item.isDailyNote || item.isGroupWeek || item.isMeta) continue;
        if (!byDate.has(item.workDate)) byDate.set(item.workDate, []);
        byDate.get(item.workDate).push(item);
    }

    return Array.from(byDate.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([workDate, items]) => ({
            workDate,
            items: items.sort((a, b) => (a.location || '').localeCompare(b.location || '')),
        }));
}

const GROUP_WEEK_PREFIX = 'group-week-';

/**
 * Расписание всей команды по неделям — для Group View.
 *
 * Лежит в том же сторе отдельными записями group-week-<воскресенье>, как и
 * заметки дня: это та же выгрузка расписания, приезжает тем же синком, а
 * отдельный стор потребовал бы поднимать версию базы на всех айпадах.
 *
 * @returns {Promise<Array<{weekEnding: string, days: string[], rows: Array, unassigned: string[], viewerRepId: ?number}>>}
 */
export async function getTeamWeeks() {
    const all = await getAllAssignments();
    return all
        .filter((item) => item.isGroupWeek)
        .sort((a, b) => a.weekEnding.localeCompare(b.weekEnding));
}

export async function getAssignment(id) {
    const db = await initializeDB();
    const tx = db.transaction(SAFETY_SCHEDULE_STORE, 'readonly');
    const item = await tx.objectStore(SAFETY_SCHEDULE_STORE).get(id);
    await tx.done;
    return item || null;
}

const META_ID = 'meta';

/**
 * Кто за айпадом, с точки зрения расписания: стоит ли в пуле, может ли вообще
 * записывать визиты (логин без карточки сотрудника — не может) и почему.
 *
 * Лежит в том же сторе отдельной записью, как и недели Group View: приезжает
 * тем же синком, а новый стор потребовал бы поднимать версию базы.
 *
 * @returns {Promise<{inPool: boolean, onList: boolean, canRecord: boolean, cannotRecordReason: ?string, repName: ?string}|null>}
 *          null — синк ещё ни разу не приносил эти сведения (старый сервер или первый запуск)
 */
export async function getMeta() {
    return getAssignment(META_ID);
}

// ─── Локальная запись ────────────────────────────────────────────────────────

/**
 * Кладёт отметку в локальный стор сразу.
 *
 * Экран не должен ждать сети: человек тапнул Arrive — время появилось.
 * На сервер это же изменение уедет отдельно, через очередь.
 */
async function putAssignment(assignment) {
    const db = await initializeDB();
    const tx = db.transaction(SAFETY_SCHEDULE_STORE, 'readwrite');
    await tx.objectStore(SAFETY_SCHEDULE_STORE).put(assignment);
    await tx.done;
}

// ─── Очередь ─────────────────────────────────────────────────────────────────

// Монотонный seq: при отправке порядок должен совпадать с порядком действий,
// а ключ opId — случайный uuid, по нему getAll вернул бы операции вперемешку.
let enqueueCounter = 0;

async function enqueueOp(payload, type = 'visit') {
    const db = await initializeDB();
    const seq = Date.now() * 1000 + (enqueueCounter++ % 1000);
    const op = {
        opId: generateUUID(),
        seq,
        type,
        payload,
        createdAt: new Date().toISOString(),
        tries: 0,
    };

    const tx = db.transaction(SAFETY_VISIT_OUTBOX_STORE, 'readwrite');
    await tx.objectStore(SAFETY_VISIT_OUTBOX_STORE).put(op);
    await tx.done;
    return op;
}

async function getOutboxOps() {
    const db = await initializeDB();
    const tx = db.transaction(SAFETY_VISIT_OUTBOX_STORE, 'readonly');
    const all = await tx.objectStore(SAFETY_VISIT_OUTBOX_STORE).getAll();
    await tx.done;
    return (all || []).sort((a, b) => (a.seq || 0) - (b.seq || 0));
}

async function deleteOp(opId) {
    const db = await initializeDB();
    const tx = db.transaction(SAFETY_VISIT_OUTBOX_STORE, 'readwrite');
    await tx.objectStore(SAFETY_VISIT_OUTBOX_STORE).delete(opId);
    await tx.done;
}

async function bumpOpTries(op) {
    const db = await initializeDB();
    const tx = db.transaction(SAFETY_VISIT_OUTBOX_STORE, 'readwrite');
    await tx.objectStore(SAFETY_VISIT_OUTBOX_STORE).put({ ...op, tries: (op.tries || 0) + 1 });
    await tx.done;
}

export async function pendingSyncCount() {
    return (await getOutboxOps()).length;
}

/**
 * uuid визитов, по которым в очереди ещё что-то лежит.
 *
 * Нужно, чтобы на карточке было видно, доехала отметка или пока нет: одного
 * общего счётчика в шапке мало — он не говорит, какая именно площадка ждёт.
 *
 * @returns {Promise<Set<string>>}
 */
export async function pendingVisitUuids() {
    const ops = await getOutboxOps();
    return new Set(ops.map((op) => op.payload?.uuid).filter(Boolean));
}

/**
 * То, что уходит на сервер про визит: всё его текущее состояние на планшете.
 *
 * clear — поля времени, которые человек стёр. Пустое время само по себе сервер
 * читает как «на планшете не отмечено» и не трогает своё; стереть можно только
 * явно, иначе отметка без времени затирала бы то, что поставила инспекция.
 */
function visitPayload(assignment, visit, clear = []) {
    return {
        uuid: visit.uuid,
        slotId: assignment.slotId,
        workDate: assignment.workDate,
        location: assignment.location,
        // Коды работ нужны серверу, чтобы посчитать Hrs Worked по таймшитам.
        // Для назначенных площадок он возьмёт их из расписания, а для добавленных
        // рэпом — по списку джобов, а вписанной руками площадке — отсюда.
        jobCodes: assignment.jobCodes || [],
        arriveAt: visit.arriveAt ?? null,
        departAt: visit.departAt ?? null,
        notes: visit.notes ?? null,
        ...(clear.length ? { clear } : {}),
        // День на айпаде, когда это сделали: очередь может дойти до сервера и
        // назавтра, а прошлое правилам — по дню действия, не по дню доставки.
        actedOn: todayKey(),
    };
}

/**
 * Гарантирует, что визит карточки есть или будет на сервере, и возвращает его uuid.
 *
 * К визиту цепляются фото, документы и инспекция, поэтому он должен дойти до
 * сервера раньше них. Визит, пришедший синком, там уже есть. Заведённый на
 * планшете — уходит операцией в очередь, если она ещё не стоит. Раньше SSI
 * придумывал визиту uuid без операции, и фото, снятое после SSI, получало
 * «Visit not found» — сервер про такой визит не знал.
 */
async function ensureVisitOnServer(assignment) {
    const latest = (await getAssignment(assignment.id)) || assignment;
    const visit = { ...(latest.visit || {}), uuid: latest.visit?.uuid || generateUUID() };

    if (!latest.visit?.uuid) {
        await putAssignment({ ...latest, visit });
    }

    if (!visit.onServer && !(await pendingVisitUuids()).has(visit.uuid)) {
        await enqueueOp(visitPayload(latest, visit));
    }

    return { assignment: { ...latest, visit }, visit };
}

/**
 * Сохраняет отметку: сначала локально, потом в очередь.
 *
 * Порядок именно такой — если приложение закроют в момент отправки, отметка
 * уже лежит на планшете и не пропадёт.
 */
export async function saveVisit(assignmentId, changes, { clear = [] } = {}) {
    const assignment = await getAssignment(assignmentId);
    if (!assignment) throw new Error('Assignment not found on this device');

    const visit = {
        ...(assignment.visit || {}),
        uuid: assignment.visit?.uuid || generateUUID(),
        ...changes,
    };
    clear.forEach((field) => { visit[field] = null; });

    await putAssignment({ ...assignment, visit });
    await enqueueOp(visitPayload(assignment, visit, clear));

    processOutbox().catch(() => {
        // Сети нет — операция осталась в очереди, это нормальный сценарий.
    });

    return visit;
}

/**
 * Своя площадка: сменить день или саму площадку.
 *
 * Только для того, что человек добавил сам, — назначенное правит офис. На
 * сервер уходит та же операция визита с новыми днём и площадкой; начатый по
 * этому визиту SSI переезжает вместе с ним, иначе сервер увидел бы в нём другую
 * площадку и завёл бы под инспекцию отдельную строку журнала.
 */
export async function updateOwnVisit(assignmentId, { workDate, location, jobCodes = [] }) {
    const assignment = await getAssignment(assignmentId);
    if (!assignment) throw new Error('Visit not found on this device');
    if (!isOwnEditable(assignment)) throw new Error('Only sites you added yourself can be changed here');

    const visit = { ...(assignment.visit || {}), uuid: assignment.visit?.uuid || generateUUID() };
    const updated = { ...assignment, workDate, location, jobCodes, visit };

    await putAssignment(updated);
    await enqueueOp(visitPayload(updated, visit));
    await moveInspectionDraft(visit.uuid, assignment, updated);

    processOutbox().catch(() => {});
    return updated;
}

/**
 * Своя ли это площадка, которую можно поменять или убрать с айпада: добавил сам,
 * SSI не сдан, день — сегодня или дальше. Прошлое и назначенное офисом не трогаем.
 */
export function isOwnEditable(assignment) {
    if (!assignment || assignment.slotId) return false;
    if (assignment.visit?.inspectionSubmitted) return false;
    if (assignment.pendingDelete || assignment.workDate < todayKey()) return false;
    // Заведённая на этом айпаде и ещё не пришедшая с сервера — своя по определению.
    if (String(assignment.id).startsWith('local-')) return true;
    return Boolean(assignment.canEdit ?? assignment.addedByRep);
}

/**
 * Черновик SSI, начатый по визиту, едет за визитом: день и площадка в связи
 * меняются, а шапка формы — только если человек её не трогал (в ней стоит
 * прежняя площадка и прежний день).
 */
async function moveInspectionDraft(visitUuid, before, after) {
    const drafts = await jobSiteSafetyInspectionService.getAllFromIndexedDB().catch(() => []);
    const draft = (drafts || []).find((d) => d.status !== 'submitted' && d.safetyVisit?.uuid === visitUuid);
    if (!draft) return;

    const next = { ...draft, safetyVisit: inspectionLink(after, after.visit || {}) };

    if (!draft.jobSite || (draft.jobSite || '').trim().toLowerCase() === (before.location || '').trim().toLowerCase()) {
        next.jobSite = after.location;
        next.jobCodes = after.jobCodes || [];
    }
    if (typeof draft.conductedOn === 'string' && draft.conductedOn.slice(0, 10) === before.workDate) {
        next.conductedOn = after.workDate + draft.conductedOn.slice(10);
    }

    await jobSiteSafetyInspectionService.saveToIndexedDB(next);
}

/**
 * Убрать свою площадку — сегодняшнюю или будущую.
 *
 * До сервера визит не доехал (операция создания ещё в очереди) — просто
 * выбрасываем его и всё, что для него лежит в очереди. Доехал — ставим в очередь
 * удаление, карточку помечаем «убирается»: исчезнет она, когда сервер подтвердит,
 * а откажет — вернётся с причиной, а не пропадёт молча.
 */
export async function removeOwnVisit(assignmentId) {
    // Очередь как раз отправляет операции этого визита — дожидаемся, чтобы не
    // решить «до сервера не доехал» за секунду до того, как он доедет.
    if (drainingPromise) await drainingPromise.catch(() => {});

    const assignment = await getAssignment(assignmentId);
    if (!assignment) return;
    if (!isOwnEditable(assignment)) throw new Error('Only your own sites for today or later can be removed here');

    const uuid = assignment.visit?.uuid;
    const ops = uuid ? (await getOutboxOps()).filter((op) => op.payload?.uuid === uuid) : [];
    const notSentYet = String(assignment.id).startsWith('local-') && ops.some((op) => op.type === 'visit');

    if (!uuid || notSentYet) {
        for (const op of ops) await deleteOp(op.opId);
        await deleteAssignment(assignmentId);
        return;
    }

    await putAssignment({ ...assignment, pendingDelete: true, visit: { ...assignment.visit, syncError: undefined } });
    await enqueueOp({ uuid, actedOn: todayKey() }, 'deleteVisit');
    processOutbox().catch(() => {});
}

async function deleteAssignment(id) {
    const db = await initializeDB();
    const tx = db.transaction(SAFETY_SCHEDULE_STORE, 'readwrite');
    await tx.objectStore(SAFETY_SCHEDULE_STORE).delete(id);
    await tx.done;
}

/**
 * Локация, которой нет в расписании: рэп заехал сам.
 *
 * Живёт как обычное назначение с slotId = null, чтобы экран не различал
 * два вида строк, а сервер отличил их по отсутствию слота.
 */
export async function addOwnLocation(workDate, location, jobCodes = []) {
    const uuid = generateUUID();
    const assignment = {
        id: `local-${uuid}`,
        slotId: null,
        workDate,
        location,
        jobCodes,
        scheduleNotes: null,
        addedByRep: true,
        visit: { uuid, arriveAt: null, departAt: null, notes: null, scorePercent: null, photoCount: 0 },
    };

    await putAssignment(assignment);
    await enqueueOp(visitPayload(assignment, assignment.visit));

    processOutbox().catch(() => {});

    return assignment;
}

/**
 * SSI с карточки.
 *
 * Возвращает `{ uuid }` черновика инспекции, который надо открыть, или
 * `{ submitted: true, scorePercent }`, если инспекция по этому визиту уже сдана:
 * открывать её снова нельзя — форма не нашла бы сданный черновик на планшете,
 * показала бы пустую и второй сдачей переписала бы первую.
 *
 * В черновик кладётся связь с визитом (safetyVisit). Она уходит на сервер вместе
 * с формой, и инспекция встаёт в строку этого визита, а не второй строкой журнала.
 * Форма принимает только `?uuid=` и читает черновик из IndexedDB, поэтому шапку
 * (площадка, коды работ, время обхода) заполняем здесь.
 */
export async function startInspection(assignment) {
    const current = (await getAssignment(assignment.id)) || assignment;

    if (current.visit?.inspectionSubmitted) {
        return { submitted: true, scorePercent: current.visit.scorePercent ?? null };
    }

    const { assignment: linked, visit } = await ensureVisitOnServer(current);
    processOutbox().catch(() => {});

    // Черновик для этого визита уже начат — открываем его, а не заводим второй.
    // Ищем по связи, а не по uuid на карточке: карточку перезаписывает синк.
    const drafts = await jobSiteSafetyInspectionService.getAllFromIndexedDB().catch(() => []);
    const existing = (drafts || []).find((d) => d.status !== 'submitted'
        && (d.safetyVisit?.uuid === visit.uuid || (visit.inspectionUuid && d.uuid === visit.inspectionUuid)));

    if (existing) {
        if (!existing.safetyVisit) {
            await jobSiteSafetyInspectionService.saveToIndexedDB({ ...existing, safetyVisit: inspectionLink(linked, visit) });
        }
        return { uuid: existing.uuid };
    }

    const uuid = generateUUID();
    const arrive = visit.arriveAt;
    const conductedOn = arrive
        ? `${linked.workDate}T${arrive}`
        : `${linked.workDate}T${new Date().toTimeString().slice(0, 5)}`;

    await jobSiteSafetyInspectionService.saveToIndexedDB({
        uuid,
        status: 'draft',
        conductedOn,
        jobSite: linked.location,
        jobCodes: linked.jobCodes || [],
        safetyVisit: inspectionLink(linked, visit),
        createdAt: new Date().toISOString(),
    });

    await putAssignment({ ...linked, visit: { ...visit, inspectionUuid: uuid } });

    return { uuid };
}

function inspectionLink(assignment, visit) {
    return {
        uuid: visit.uuid,
        slotId: assignment.slotId ?? null,
        workDate: assignment.workDate,
        location: assignment.location,
        jobCodes: assignment.jobCodes || [],
    };
}

/**
 * Инспекция по визиту сдана — карточка зеленеет сразу, без ожидания синка.
 *
 * Форма после сдачи удаляет черновик с планшета, так что взять счёт потом
 * будет неоткуда: помечаем визит в момент сдачи. Следующий синк принесёт то же
 * самое с сервера.
 */
export async function markInspectionSubmitted(visitUuid, { inspectionUuid, scorePercent }) {
    if (!visitUuid) return;

    const all = await getAllAssignments();
    const target = all.find((a) => a.visit?.uuid === visitUuid);
    if (!target) return;

    await putAssignment({
        ...target,
        visit: {
            ...target.visit,
            inspectionUuid,
            inspectionSubmitted: true,
            scorePercent: scorePercent === null || scorePercent === undefined ? null : String(scorePercent),
        },
    });
}

// ─── Обновление с сервера ────────────────────────────────────────────────────

/**
 * Кладёт ответ сервера в локальный стор. Общая точка для синка и для
 * обновления при открытии экрана — иначе две реализации разъехались бы.
 */
export async function writeScheduleToStore(db, storeName, payload) {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);

    // Пометки «не сохранилось» живут только на планшете. Полная замена стёрла бы
    // их, и рэп перестал бы видеть, что отметка не дошла, — переносим их на
    // новые записи, а карточки, которые так и не доехали до сервера, оставляем.
    const previous = await store.getAll();
    const errorsByVisit = new Map();
    const errorsById = new Map();
    const unsavedLocal = [];
    const unsavedNotes = new Map();
    for (const item of previous || []) {
        if (item.isDailyNote && item.syncError) {
            unsavedNotes.set(item.workDate, item);
            continue;
        }
        const error = item.visit?.syncError;
        if (!error) continue;
        if (item.visit?.uuid) errorsByVisit.set(item.visit.uuid, error);
        errorsById.set(item.id, error);
        if (String(item.id).startsWith('local-')) unsavedLocal.push(item);
    }

    // Полная замена, а не merge: планировщик мог снять человека с площадки,
    // и снятое назначение должно исчезнуть с планшета, а не остаться висеть.
    await store.clear();

    const serverVisitUuids = new Set();
    for (const assignment of (payload?.assignments || [])) {
        let record = assignment;
        if (assignment.visit) {
            serverVisitUuids.add(assignment.visit.uuid);
            // onServer — визит уже есть на сервере: к нему можно сразу цеплять
            // фото и инспекцию, без операции «завести визит» в очереди.
            record = { ...assignment, visit: { ...assignment.visit, onServer: true } };
        }
        const error = (record.visit && errorsByVisit.get(record.visit.uuid)) || errorsById.get(record.id);
        if (error) {
            record = { ...record, visit: { ...(record.visit || {}), syncError: error } };
        }
        await store.put(record);
    }

    for (const item of unsavedLocal) {
        if (!serverVisitUuids.has(item.visit?.uuid)) {
            await store.put(item);
        }
    }

    for (const [workDate, notes] of Object.entries(payload?.dailyNotes || {})) {
        const unsaved = unsavedNotes.get(workDate);
        await store.put({ id: `daily-note-${workDate}`, isDailyNote: true, workDate, notes, ...(unsaved ? { syncError: unsaved.syncError } : {}) });
        unsavedNotes.delete(workDate);
    }
    // Заметка, которую сервер так и не принял, остаётся на экране с пометкой.
    for (const note of unsavedNotes.values()) {
        await store.put(note);
    }

    // Расписание команды. viewerRepId кладём в каждую неделю: по нему Group View
    // подсвечивает свои дни, а у человека вне пула он null — подсвечивать нечего.
    for (const week of (payload?.group || [])) {
        await store.put({
            ...week,
            id: `${GROUP_WEEK_PREFIX}${week.weekEnding}`,
            isGroupWeek: true,
            viewerRepId: payload?.repId ?? null,
        });
    }

    // Кто за айпадом. Старый сервер canRecord не присылает — тогда записывать
    // можно, как и было до него.
    await store.put({
        id: META_ID,
        isMeta: true,
        inPool: Boolean(payload?.inPool),
        onList: Boolean(payload?.onList ?? payload?.inPool),
        canRecord: payload?.canRecord !== false,
        cannotRecordReason: payload?.cannotRecordReason || null,
        repName: payload?.repName || null,
    });

    await tx.done;
}

/**
 * Запись ответа синка — только когда очередь пуста.
 *
 * «Sync All Data» затирал стор целиком, даже если в очереди лежали отметки,
 * сделанные без связи: время пропадало с карточек до следующего обновления, а
 * пометку об ошибке очередь потом было не на что повесить. Сначала отправляем
 * накопленное; не разошлось — оставляем на экране то, что есть на планшете.
 */
export async function applySyncPayload(db, storeName, payload) {
    await processOutbox();
    if (await pendingSyncCount() > 0) {
        return 'pending';
    }

    await writeScheduleToStore(db, storeName, payload);
    return 'updated';
}

/**
 * Подтягивает расписание при открытии экрана, если есть сеть.
 *
 * Сначала отправляем накопленное, и только потом тянем: обновление затирает
 * стор целиком, и неотправленная отметка иначе пропала бы с экрана. Если
 * очередь не разошлась — не трогаем локальные данные вовсе.
 *
 * @returns {Promise<'updated'|'offline'|'pending'|'failed'>}
 */
export async function refreshFromServer() {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return 'offline';
    }

    await processOutbox();
    if (await pendingSyncCount() > 0) {
        return 'pending';
    }

    try {
        const response = await axiosInstance.get('/safety-schedule');
        const payload = response?.data?.data ?? response?.data;

        const db = await initializeDB();
        const result = await applySyncPayload(db, SAFETY_SCHEDULE_STORE, payload);
        if (result === 'updated') {
            localStorage.setItem('lastSafetyScheduleSyncTime', new Date().toISOString());
        }

        return result;
    } catch (e) {
        // Сеть моргнула — на экране остаётся то, что уже лежит на планшете.
        console.warn('[SafetySchedule] refresh failed', e);
        return 'failed';
    }
}

// ─── Заметки за день ─────────────────────────────────────────────────────────

const DAILY_NOTE_PREFIX = 'daily-note-';

/**
 * Заметка на весь день — «Add Daily Notes» с эскиза.
 *
 * Живёт в том же сторе отдельной записью с ключом daily-note-<дата>: экран
 * читает её вместе с назначениями, и лишний стор ради одной строки не нужен.
 */
export async function getDailyNote(workDate) {
    const db = await initializeDB();
    const tx = db.transaction(SAFETY_SCHEDULE_STORE, 'readonly');
    const row = await tx.objectStore(SAFETY_SCHEDULE_STORE).get(DAILY_NOTE_PREFIX + workDate);
    await tx.done;
    return row?.notes || '';
}

/** Все заметки дня на планшете: workDate → текст. */
export async function getDailyNotes() {
    const all = await getAllAssignments();
    return Object.fromEntries(all.filter((item) => item.isDailyNote && item.notes).map((item) => [item.workDate, item.notes]));
}

/** Заметки дня, которые сервер не принял: workDate → причина. */
export async function getDailyNoteErrors() {
    const all = await getAllAssignments();
    return Object.fromEntries(all.filter((item) => item.isDailyNote && item.syncError).map((item) => [item.workDate, item.syncError]));
}

export async function saveDailyNote(workDate, notes) {
    const db = await initializeDB();
    const tx = db.transaction(SAFETY_SCHEDULE_STORE, 'readwrite');
    await tx.objectStore(SAFETY_SCHEDULE_STORE).put({
        id: DAILY_NOTE_PREFIX + workDate,
        isDailyNote: true,
        workDate,
        notes: notes || '',
    });
    await tx.done;

    await enqueueOp({ workDate, notes: notes || '' }, 'dailyNote');
    processOutbox().catch(() => {});
}

// ─── Фото и документы ────────────────────────────────────────────────────────

/** «Ability to Add Pictures (10 per) or Documents» — десять файлов на визит. */
export const MAX_FILES_PER_VISIT = 10;

/** Документ крупнее не влезет в очередь планшета и в запрос на сервер. */
export const MAX_DOCUMENT_BYTES = 15 * 1024 * 1024;

/**
 * Файл с площадки — снимок камеры или документ.
 *
 * Кладём в очередь целиком, в base64: на планшете без связи файла на диске
 * сервера ещё нет, а файл пропасть не должен. Локальный счётчик двигаем
 * сразу — иначе рэп не поймёт, сколько уже приложил.
 *
 * @param {string} assignmentId
 * @param {{ dataUrl: string, name?: string }} file
 */
export async function addFile(assignmentId, { dataUrl, name = null }) {
    const assignment = await getAssignment(assignmentId);
    if (!assignment) throw new Error('Assignment not found on this device');

    const count = assignment.visit?.photoCount || 0;
    if (count >= MAX_FILES_PER_VISIT) {
        throw new Error(`This visit already has ${MAX_FILES_PER_VISIT} files`);
    }

    // Файл цепляется к визиту, поэтому визит обязан дойти до сервера раньше.
    const { assignment: linked, visit } = await ensureVisitOnServer(assignment);
    await putAssignment({ ...linked, visit: { ...visit, photoCount: count + 1 } });

    await enqueueOp({ uuid: visit.uuid, file: dataUrl, name }, 'photo');
    processOutbox().catch(() => {});
}

/**
 * Снимки и документы визита, которые ещё ждут отправки, — чтобы под скрепкой они
 * были видны сразу, а не только после того, как дойдут до сервера.
 *
 * @returns {Promise<Array<{opId: string, name: ?string, dataUrl: string, createdAt: string}>>}
 */
export async function getQueuedFiles(visitUuid) {
    if (!visitUuid) return [];
    return (await getOutboxOps())
        .filter((op) => op.type === 'photo' && op.payload?.uuid === visitUuid)
        .map((op) => ({
            opId: op.opId,
            name: op.payload.name || null,
            dataUrl: op.payload.file || op.payload.photo || '',
            createdAt: op.createdAt,
        }));
}

/**
 * Содержимое файла для просмотра: ещё не отправленный — прямо с айпада, без
 * сети; уже на сервере — скачивается (это файл, а не данные экрана).
 *
 * @returns {Promise<Blob>}
 */
export async function loadFileBlob(file) {
    if (file.dataUrl) {
        return (await fetch(file.dataUrl)).blob();
    }
    const response = await axiosInstance.get(`/safety-schedule/visit/file/${file.id}`, { responseType: 'blob' });
    return response.data;
}

/**
 * Выбрасывает операцию из очереди — но не молча.
 *
 * Раньше отклонённая отметка просто исчезала с console.warn: рэп видел время
 * на экране и был уверен, что оно сохранено. Теперь на карточке остаётся
 * пометка, что именно не доехало и почему.
 */
async function dropOp(op, reason) {
    console.warn('[SafetyVisitOutbox] dropping op', op.type, op.opId, reason);
    await deleteOp(op.opId);

    // У заметки дня нет визита — пометку вешаем на саму заметку, иначе отказ
    // сервера пропадал бы без следа.
    if (op.type === 'dailyNote' && op.payload?.workDate) {
        const note = await getAssignment(DAILY_NOTE_PREFIX + op.payload.workDate);
        await putAssignment({
            ...(note || { id: DAILY_NOTE_PREFIX + op.payload.workDate, isDailyNote: true, workDate: op.payload.workDate, notes: op.payload.notes || '' }),
            syncError: `Day notes did not save: ${reason}`,
        });
        return;
    }

    const uuid = op.payload?.uuid;
    if (!uuid) return;

    const all = await getAllAssignments();
    const target = all.find((a) => a.visit?.uuid === uuid);
    if (!target) return;

    if (op.type === 'deleteVisit') {
        const restored = { ...target, visit: { ...target.visit, syncError: `Could not remove this visit: ${reason}` } };
        delete restored.pendingDelete;
        await putAssignment(restored);
        return;
    }

    const what = { photo: 'File', dailyNote: 'Day notes' }[op.type] || 'Times and notes';
    await putAssignment({
        ...target,
        visit: { ...target.visit, syncError: `${what} did not save: ${reason}` },
    });
}

async function clearDailyNoteError(workDate) {
    if (!workDate) return;
    const note = await getAssignment(DAILY_NOTE_PREFIX + workDate);
    if (!note?.syncError) return;
    const next = { ...note };
    delete next.syncError;
    await putAssignment(next);
}

/** Сервер подтвердил удаление — карточка уходит с айпада. */
async function removeAssignmentOfVisit(uuid) {
    if (!uuid) return;
    const target = (await getAllAssignments()).find((a) => a.visit?.uuid === uuid);
    if (target) await deleteAssignment(target.id);
}

/**
 * Файл дошёл — сразу встаёт в список под скрепкой, не дожидаясь следующего
 * синка: иначе он пропал бы из «ждёт отправки» и нигде не появился.
 */
async function addSentFile(op, data) {
    const uuid = op.payload?.uuid;
    const target = uuid ? (await getAllAssignments()).find((a) => a.visit?.uuid === uuid) : null;
    if (!target) return;

    const visit = { ...target.visit };
    delete visit.syncError;
    if (data?.documentId && !(visit.files || []).some((f) => f.id === data.documentId)) {
        const pdf = (op.payload.file || op.payload.photo || '').startsWith('data:application/pdf');
        visit.files = [...(visit.files || []), {
            id: data.documentId,
            name: op.payload.name || (pdf ? 'Document.pdf' : 'Photo.jpg'),
            kind: pdf ? 'other' : 'photo',
            mimeType: pdf ? 'application/pdf' : 'image/jpeg',
            createdAt: new Date().toISOString(),
        }];
    }
    if (typeof data?.photoCount === 'number') visit.photoCount = data.photoCount;
    await putAssignment({ ...target, visit });
}

async function clearSyncError(uuid) {
    if (!uuid) return;
    const all = await getAllAssignments();
    const target = all.find((a) => a.visit?.uuid === uuid && a.visit?.syncError);
    if (!target) return;
    const visit = { ...target.visit };
    delete visit.syncError;
    await putAssignment({ ...target, visit });
}

// Промис текущей отправки, а не просто флаг: если очередь уже проигрывается,
// вызывающий должен дождаться ЕЁ, а не получить сразу ноль. Иначе счётчик
// «сколько осталось отправить» читается до конца отправки и врёт.
let drainingPromise = null;

/**
 * Проигрывает очередь на сервер. Идемпотентно — сервер дедупит по uuid,
 * поэтому повтор безопасен.
 */
export function processOutbox() {
    if (drainingPromise) return drainingPromise;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return Promise.resolve(0);

    drainingPromise = drainOutbox().finally(() => { drainingPromise = null; });
    return drainingPromise;
}

/** Ответы, которые повтор не исправит: сервер понял запрос и отказал. */
const PERMANENT_STATUSES = new Set([400, 403, 404, 409, 413, 422]);

/** Сколько раз терпим ошибку самого сервера (5xx), прежде чем показать её на карточке. */
const MAX_SERVER_ERROR_TRIES = 10;

async function drainOutbox() {
    let done = 0;

    try {
        for (const op of await getOutboxOps()) {
            try {
                const url = {
                    dailyNote: '/safety-schedule/daily-note',
                    photo: '/safety-schedule/visit/photo',
                    deleteVisit: '/safety-schedule/visit/delete',
                }[op.type] || '/safety-schedule/visit';

                const response = await axiosInstance.post(url, op.payload);
                await deleteOp(op.opId);
                if (op.type === 'dailyNote') {
                    await clearDailyNoteError(op.payload?.workDate);
                } else if (op.type === 'deleteVisit') {
                    await removeAssignmentOfVisit(op.payload?.uuid);
                } else if (op.type === 'photo') {
                    await addSentFile(op, response?.data);
                } else {
                    await clearSyncError(op.payload?.uuid);
                }
                done++;
            } catch (e) {
                const status = e?.response?.status;
                // Наш API отказывает с JSON-сообщением. 404 без него — это не
                // «визита нет», а роут не нашёлся (прокси, сервер без обновления):
                // такой ответ выбросил бы все отметки разом, поэтому он не окончательный.
                const permanent = PERMANENT_STATUSES.has(status)
                    && !(status === 404 && !e?.response?.data?.message);

                if (permanent) {
                    // Постоянная ошибка: день в будущем, чужой визит, удалённый в
                    // офисе, файл не того типа. Повторять бессмысленно — иначе
                    // очередь встанет навсегда.
                    await dropOp(op, e?.response?.data?.message || 'rejected by the server');
                    continue;
                }

                // Нет ответа вовсе (сеть на площадке, Wi-Fi без интернета) или
                // сессия истекла (401) — это не вина операции. Попытки не считаем
                // и ничего не выбрасываем: раньше восемь таких промахов подряд
                // (четыре минуты плохой связи) стирали отметку из очереди.
                if (!status || status === 401) {
                    break;
                }

                // Сервер ответил ошибкой. Не бесконечно: «ядовитая» операция не
                // должна держать всю очередь, но и выброшена она будет с пометкой.
                if ((op.tries || 0) + 1 >= MAX_SERVER_ERROR_TRIES) {
                    await dropOp(op, e?.response?.data?.message || `server error ${status}`);
                    continue;
                }

                await bumpOpTries(op);
                break;
            }
        }
    } catch (e) {
        console.warn('[SafetyVisitOutbox] drain failed', e);
    }

    return done;
}

// Очередь уходит и тогда, когда экран расписания закрыт: рэп отметился без
// связи и пошёл заполнять другие формы — отметка должна уехать, как только
// появится сеть, а не когда он снова откроет My Schedule.
// То же при старте приложения и когда айпад просыпается: события online при этом
// не бывает, сеть просто уже есть.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
    window.addEventListener('online', () => { processOutbox().catch(() => {}); });
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') processOutbox().catch(() => {});
    });
    setTimeout(() => { processOutbox().catch(() => {}); }, 5000);
}

const safetyScheduleService = {
    getScheduleByDay,
    getTeamWeeks,
    getAssignment,
    getMeta,
    saveVisit,
    updateOwnVisit,
    isOwnEditable,
    removeOwnVisit,
    addOwnLocation,
    startInspection,
    markInspectionSubmitted,
    refreshFromServer,
    getDailyNote,
    getDailyNotes,
    getDailyNoteErrors,
    saveDailyNote,
    addFile,
    getQueuedFiles,
    loadFileBlob,
    processOutbox,
    pendingSyncCount,
    pendingVisitUuids,
};

export default safetyScheduleService;
